import {
  ArchiveIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  LockIcon,
  type LucideIcon,
} from "lucide-react"
import {
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react"
import { Link, useLocation, useNavigate, useNavigationType } from "react-router"
import { Badge } from "@/primitives/badge.tsx"
import { Button, buttonVariants } from "@/primitives/button.tsx"
import { Separator } from "@/primitives/separator.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { ButtonLink } from "./button-link.tsx"
import {
  Disc,
  IdentityTile,
  WithPresence,
  type PersonShown,
  type PresenceShown,
} from "./identity.tsx"
import { Kbd, useCopied } from "./key.tsx"
import { BeaconMark } from "./marks.tsx"
import { MenuConfirmationContext } from "./menu-confirmation.ts"
import {
  backOf,
  compactEndOf,
  documentTitleOf,
  ladderOf,
  opensInPlace,
  type ChromeLayer,
  type Ladder,
  type MountedChrome,
  type PageBack,
} from "./page-chrome.ts"
import {
  arrivingScroll,
  handsBack,
  takesFocus,
  type FocusAt,
  type FocusFrom,
} from "./page-scroll.ts"
import { dockOf, SaveBarDockContext } from "./save-bar-dock.ts"
import { RowActions, type RowAction } from "./row-actions.tsx"
import { Toggletip } from "./toggletip.tsx"
import { useViewport } from "./viewport.tsx"

/*
 * Every page's frame, top to bottom: a PageHeader naming where the Member is, an optional
 * PageToolbar holding the page's filters and view options, and a PageBody at one of six widths.
 *
 * The header and toolbar stick to the top of the page's one scroll container, and a sticky header
 * inside a list (a table's head, a GroupHeader) must stick under them. Rather than each list
 * guessing, the header (with its tab row on the compact layout) and the toolbar register
 * themselves in PageChromeContext as they mount, and a list asks `useStickyTop()` where to stick.
 */

type PageChrome = {
  ladder: Ladder
  register: (layer: ChromeLayer) => () => void
  /** The page is a tab's own page on the compact layout, which its header gives no back. */
  tabRoot: boolean
}

/**
 * What chrome the page has mounted. Outside a `PageScroll` nothing registers, and a list sticks
 * under the header, which every page has.
 */
export const PageChromeContext = createContext<PageChrome>({
  ladder: ladderOf({ header: 0, tabs: 0, toolbar: 0, anchors: 0 }),
  register: () => () => undefined,
  tabRoot: false,
})

/**
 * The inset panel's one scroll container, and the context the chrome inside it registers in. The
 * shell renders one around the routed page; nothing else in the panel scrolls vertically, except a
 * Board's own scroller.
 *
 * It is what the keyboard scrolls, too: it takes focus as each page arrives, unless something else
 * has it (`takesFocus`), without moving, and without a ring, since it is the page and not a control
 * on it. Tab goes on from it into the page, starting at the header.
 *
 * Its `ref` is a handle that returns the page to its top, as tapping the current tab does on the
 * compact layout. There it keeps an overscroll to itself, so a flick past the end never drags the
 * shell around it, and holds the page's SaveBar dock: while bars dock at its foot it pads its end,
 * and what focus scrolls into view, by them and a 24px gap, so nothing ends under them. `tabRoot`
 * says the page is a tab's own page (Home, My tickets, Projects), whose compact header has no back
 * chevron. On compact it also keeps each entry's place (`arrivingScroll`): a page opened starts at
 * its top, and Back returns a list to where it was left, as a ticket opened from it is left.
 */
export function PageScroll({
  ref,
  tabRoot = false,
  children,
}: {
  ref?: Ref<PageScrollHandle>
  tabRoot?: boolean
  children: ReactNode
}) {
  const [mounted, setMounted] = useState<MountedChrome>({
    header: 0,
    tabs: 0,
    toolbar: 0,
    anchors: 0,
  })
  const register = useCallback((layer: ChromeLayer) => {
    setMounted((now) => ({ ...now, [layer]: now[layer] + 1 }))
    return () => setMounted((now) => ({ ...now, [layer]: now[layer] - 1 }))
  }, [])
  const chrome = useMemo(
    () => ({ ladder: ladderOf(mounted), register, tabRoot }),
    [mounted, register, tabRoot],
  )
  const scroller = useRef<HTMLDivElement>(null)
  const onFocus = useKeyboardScroll(scroller)
  const { compact } = useViewport()
  useEntryScroll(scroller, compact)
  const [dock] = useState(dockOf)
  const docked = useSyncExternalStore(dock.subscribe, dock.height)
  const clearOfDock = docked > 0 ? docked + 24 : undefined
  useImperativeHandle(
    ref,
    () => ({
      scrollToTop: () =>
        scroller.current?.scrollTo({
          top: 0,
          behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        }),
    }),
    [],
  )

  return (
    <PageChromeContext value={chrome}>
      <SaveBarDockContext value={dock}>
        <div
          ref={scroller}
          data-slot="page-scroll"
          tabIndex={-1}
          onFocus={onFocus}
          style={{ paddingBottom: clearOfDock, scrollPaddingBottom: clearOfDock }}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto outline-none compact:overscroll-contain"
        >
          {children}
        </div>
      </SaveBarDockContext>
    </PageChromeContext>
  )
}

/**
 * Keeps where `scroller` stands on each history entry, and, while `enabled`, places it as an entry
 * arrives (`arrivingScroll`). A list returned to may still be drawing its rows as it arrives, so it
 * tries again on the frames that follow until the list is tall enough, half a second at most.
 */
function useEntryScroll(scroller: RefObject<HTMLDivElement | null>, enabled: boolean) {
  const { key, pathname } = useLocation()
  const arrival = useNavigationType()
  const saved = useRef(new Map<string, number>())
  const at = useRef({ key, pathname })

  // Under the entry shown: as an arriving page swaps the content, the place it is left at is the
  // next entry's, since the entry changes before the browser says the scroll moved.
  useEffect(() => {
    const element = scroller.current
    if (!element) return
    const keep = () => saved.current.set(at.current.key, element.scrollTop)
    element.addEventListener("scroll", keep, { passive: true })
    return () => element.removeEventListener("scroll", keep)
  }, [scroller])

  useLayoutEffect(() => {
    const before = at.current
    if (before.key === key) return
    at.current = { key, pathname }
    const element = scroller.current
    if (!enabled || !element) return
    const top = arrivingScroll({
      arrival,
      samePage: before.pathname === pathname,
      saved: saved.current.get(key),
    })
    if (top === "kept") return
    let frames = 0
    let frame = 0
    const place = () => {
      element.scrollTop = top
      if (element.scrollTop < top - 1 && ++frames < 30) frame = requestAnimationFrame(place)
    }
    place()
    return () => cancelAnimationFrame(frame)
  }, [key, pathname, arrival, enabled, scroller])
}

/** What the shell can ask of the page's scroll container. */
export type PageScrollHandle = { scrollToTop: () => void }

/**
 * Makes `scroller` what the keyboard scrolls: it takes focus as each page arrives, unless something
 * else has it, and takes it back from an overlay closing on the focus it held. Gives back the
 * handler for its focus events.
 */
function useKeyboardScroll(scroller: RefObject<HTMLDivElement | null>) {
  const { pathname } = useLocation()
  // The path focus inside the page was last taken on, so a link followed from the page before
  // is told apart from a field the new page focused as it opened.
  const focusedOn = useRef(pathname)
  // Whether the scroller itself holds the page's focus, rather than a control on the page.
  const heldItself = useRef(false)

  // After the page's own effects, so a field it focuses as it opens already has focus.
  useEffect(() => {
    const element = scroller.current
    if (element && takesFocus(focusAt(element, focusedOn.current), pathname))
      element.focus({ preventScroll: true })
  }, [scroller, pathname])

  return (event: FocusEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    // A page's dialogs are its children in React but portalled out of it in the DOM, so focus in
    // them is not the page's.
    if (!element.contains(event.target)) return
    const from = focusFrom(event.relatedTarget)
    if (event.target !== element && handsBack(heldItself.current, from)) {
      element.focus({ preventScroll: true })
      return
    }
    heldItself.current = event.target === element
    focusedOn.current = pathname
  }
}

/** Where focus is now, as `takesFocus` asks, `page` being the page scroll. */
function focusAt(page: Element, focusedOn: string): FocusAt {
  const active = document.activeElement
  if (!active || active === document.body) return "nowhere"
  if (page.contains(active)) return { page: focusedOn }
  // Chrome focuses a link the pointer clicks, but without the ring it shows the keyboard's focus.
  return active.matches("a[href]:not(:focus-visible)") ? "clicked" : "elsewhere"
}

/** Where focus came from: an overlay (Base UI portals every one), or nowhere, as from one gone. */
const focusFrom = (previous: EventTarget | null): FocusFrom =>
  previous === null ||
  (previous instanceof Element && previous.closest("[data-base-ui-portal]") !== null)
    ? "overlay"
    : "elsewhere"

/** The `top-*` class an in-list sticky header takes, so it sticks under the page's chrome. */
export function useStickyTop() {
  return useContext(PageChromeContext).ladder.underChrome
}

/**
 * Marks what it holds as inside a scroller of its own, under the page's chrome rather than in the
 * page scroll, as each Status's page of a phone's Board is: an in-list header there
 * (`useStickyTop()`) sticks to that scroller's top.
 */
export function OwnScroller({ children }: { children: ReactNode }) {
  const chrome = useContext(PageChromeContext)
  const own = useMemo(
    () => ({ ...chrome, ladder: { ...chrome.ladder, underChrome: "top-0" as const } }),
    [chrome],
  )
  return <PageChromeContext value={own}>{children}</PageChromeContext>
}

/** How wide the desk's PageHeader stands, in px; none before it is laid out and on `compact`. */
const HeaderWidthContext = createContext<number | undefined>(undefined)

/**
 * How wide the desk's PageHeader stands, for its `controls` to fold to where its row has no room
 * (Insights' range a Select in a narrow window, or beside an open sidebar). Read inside the
 * controls; none before the header is laid out and on `compact`, where the controls have a row
 * of their own.
 */
export function useHeaderWidth() {
  return useContext(HeaderWidthContext)
}

/** A ref that keeps `width` at the element's laid-out width as it resizes. */
function useMeasuredWidth() {
  const [width, setWidth] = useState<number>()
  const measure = useCallback((element: HTMLElement | null) => {
    if (!element || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.round(entry.contentRect.width))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return [width, measure] as const
}

/**
 * The sticky ladder's rungs for the page's chrome, for what sticks under the header itself rather
 * than inside a list: the toolbar, a split page's panes, a ticket's drop target.
 */
export function useLadder() {
  return useContext(PageChromeContext).ladder
}

/**
 * Registers a layer of chrome for as long as it is mounted and `shown`, before the page paints.
 * Internal to the design system: the PageHeader, the PageToolbar and the compact AnchorNav.
 */
export function useRegistered(layer: ChromeLayer, shown = true) {
  const { register } = useContext(PageChromeContext)
  useLayoutEffect(() => (shown ? register(layer) : undefined), [register, layer, shown])
}

/**
 * One step of the breadcrumb. `href` makes it a link back up (with `state` passed on, such as the
 * Board a ticket was opened from); `glyph` puts a Project's tile or an icon before its label;
 * `count` follows it, as in "Members 68"; `copy` makes it a button that copies that value, as a
 * ticket's key does. A `person` glyph is a Member's disc, with a dot for their `presence` when
 * given, as a direct message is named by who it is with; a `beacon` glyph is Beacon's mark, as a
 * Member's Beacon DM is named by Beacon; `detail` follows the label in the quiet
 * tier on the desk, as their presence and status in words, and is the compact bar's second line
 * under the page's title. `title` is what the compact bar calls the page when it is the last
 * crumb and its label says little without the crumbs before it: "Change request 2" for "2". An empty `title`
 * leaves the bar untitled, for a page whose body heads itself with the same name, as a group's does
 * where it is renamed; the label is still the page's h1, for assistive technology alone.
 */
export type Crumb = {
  label: string
  title?: string
  href?: string
  state?: unknown
  glyph?:
    | { kind: "project"; projectKey: string }
    | { kind: "icon"; icon: LucideIcon }
    | { kind: "person"; person: PersonShown; presence?: PresenceShown | undefined }
    | { kind: "beacon" }
  count?: number
  detail?: string
  copy?: string
}

/** A tab in the header: a link to one of a page's sibling views, such as a Project's Settings. */
export type HeaderTab = { label: string; href: string; current: boolean }

// A crumb squeezed past its name clips its glyph too, rather than drawing it over its neighbour.
const crumbClass =
  "flex h-7 min-w-0 items-center overflow-hidden gap-1.5 rounded-md px-1.5 text-ui font-medium transition-colors duration-100 ease-snappy focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"

/** A person crumb's disc, decorating the name beside it, with their presence dot when given. */
function PersonGlyph({
  glyph,
  size,
}: {
  glyph: { person: PersonShown; presence?: PresenceShown | undefined }
  size: 16 | 20
}) {
  const disc = (
    <span aria-hidden className="inline-flex shrink-0">
      <Disc person={glyph.person} size={size} />
    </span>
  )
  return glyph.presence ? (
    <WithPresence presence={glyph.presence} size={size}>
      {disc}
    </WithPresence>
  ) : (
    disc
  )
}

function CrumbContent({ crumb }: { crumb: Crumb }) {
  const { glyph } = crumb
  return (
    <>
      {glyph?.kind === "project" && <IdentityTile projectKey={glyph.projectKey} size={16} />}
      {glyph?.kind === "icon" && <glyph.icon aria-hidden className="size-4 shrink-0" />}
      {glyph?.kind === "person" && <PersonGlyph glyph={glyph} size={16} />}
      {glyph?.kind === "beacon" && <BeaconMark size={16} />}
      <span className="truncate">{crumb.label}</span>
      {crumb.count !== undefined && (
        <span className="text-xs text-subtle-foreground tabular-nums">{crumb.count}</span>
      )}
      {crumb.detail && (
        <span className="min-w-0 truncate text-xs font-normal text-subtle-foreground">
          {crumb.detail}
        </span>
      )}
    </>
  )
}

function CopyCrumb({ crumb, value }: { crumb: Crumb; value: string }) {
  const { copied, copy } = useCopied(value)
  return (
    <Tooltip>
      <TooltipTrigger
        closeOnClick={false}
        onClick={copy}
        className={cn(crumbClass, "cursor-pointer text-foreground hover:bg-accent")}
      >
        <CrumbContent crumb={crumb} />
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied" : `Copy ${value}`}</TooltipContent>
    </Tooltip>
  )
}

function CrumbItem({ crumb, last }: { crumb: Crumb; last: boolean }) {
  if (crumb.copy !== undefined) return <CopyCrumb crumb={crumb} value={crumb.copy} />
  if (crumb.href && !last)
    return (
      <Link
        to={crumb.href}
        state={crumb.state}
        className={cn(crumbClass, "text-muted-foreground hover:bg-accent hover:text-foreground")}
      >
        <CrumbContent crumb={crumb} />
      </Link>
    )
  return (
    <span
      aria-current={last ? "page" : undefined}
      className={cn(crumbClass, last ? "text-foreground" : "text-muted-foreground")}
    >
      <CrumbContent crumb={crumb} />
    </span>
  )
}

const headerBadges = {
  archived: { variant: "neutral", Icon: ArchiveIcon, label: "Archived" },
  "read-only": { variant: "outline", Icon: LockIcon, label: "Read only" },
} as const

/**
 * One of a page's actions, as data, so each layout draws it its own way: on the desk a small
 * Button with its icon and label, on `compact` an icon button or an entry in the ⋯. It does
 * `onSelect`, or goes to `href`. `shortcut` is the key that does it too, which the page binds; the
 * desk shows its Kbd. `busy` is an action under way: a spinner takes its icon's place and it takes
 * no second press. `count` follows its label, as "Possible duplicates 3". `dialog` says it opens
 * the page's dialog, as Add Member does, and whether it is open now, as a dialog's trigger tells a
 * screen reader. `barIcon` is the glyph the compact bar draws it with, alone and unlabelled, when
 * `icon` names the thing it makes rather than the making: New Epic's ZapIcon becomes a PlusIcon.
 */
export type HeaderAction = {
  label: string
  icon: LucideIcon
  barIcon?: LucideIcon
  shortcut?: ShortcutId
  onSelect?: () => void
  href?: string
  disabled?: boolean
  busy?: boolean
  count?: number
  dialog?: { open: boolean }
}

/**
 * A page's action other than its primary one, which the desk shows as a button of its own and the
 * compact layout lists in its ⋯. Its `look` on the desk: `outline` (the default), its shortcut in
 * its tooltip; `quiet`, a ghost button; `onward`, a way to another page, a ghost link with its arrow
 * after its label; or `icon`, a ghost icon button named by its tooltip, a desk shortcut to an entry
 * the ⋯ (`more`) holds too, so the compact layout leaves it out. An `icon` that `copies` a value
 * says "Copied" in its tooltip, as a copyable crumb does.
 *
 * A confirmation (`kind: "confirmation"` or `"destructive"`, as in RowActions) is a button that
 * asks first on the desk, as a change request's Abandon is, and its ⋯ entry on `compact`.
 */
export type SecondaryAction =
  | (HeaderAction & { look?: "outline" | "quiet" | "onward" | "icon"; copies?: string })
  | Extract<RowAction, { kind: "confirmation" | "destructive" }>

/**
 * What a page lets a Member do from its header, as data. On the desk, at the header's end: the
 * `controls`, the `secondary` buttons, the `primary` filled Button carrying its Kbd, and the ⋯ of
 * `more`. On `compact`: the primary as an icon button, and a ⋯ holding the secondary actions and
 * then `more`, destructive entries last, with `controls` on their own row under the bar; with no
 * primary, a ⋯ that would hold one plain entry draws it as the icon button instead
 * (`compactEndOf`). `more` is RowActions' items, confirmations and submenus included; `moreLabel`
 * names its ⋯ for a screen reader.
 */
export type PageActions = {
  primary?: HeaderAction
  secondary?: readonly SecondaryAction[]
  more?: readonly RowAction[]
  moreLabel?: string
  /** What sets the page's view rather than acting on it: Spend's range, the kitchen sink's Theme. */
  controls?: ReactNode
}

/** What a button tells a screen reader about the dialog its action opens, open or not. */
const dialogTrigger = ({ dialog }: HeaderAction) =>
  dialog && ({ "aria-haspopup": "dialog", "aria-expanded": dialog.open } as const)

/** A secondary action the ⋯ also offers on its own: the desk's icon shortcut. */
const deskOnly = (action: SecondaryAction) => "look" in action && action.look === "icon"

/** Whether there is anything to draw at the header's end. */
const anyOf = (actions: PageActions | undefined) =>
  actions !== undefined &&
  (actions.primary !== undefined ||
    (actions.secondary?.length ?? 0) > 0 ||
    (actions.more?.length ?? 0) > 0 ||
    actions.controls !== undefined)

/**
 * The top of every page, stuck there, naming where the Member is and holding what they can do.
 *
 * On the desk it is 48px tall: the breadcrumb, any `badges` saying the page can't be changed, the
 * page's sibling `tabs`, and its `actions` at the end. There is no page title besides the last
 * crumb, which is the page's h1 at the crumbs' size. The crumbs give way first where the row is
 * short: those above the page drop out whole, the farthest first, then the page's name truncates;
 * `controls` that would crowd it read its width (`useHeaderWidth`) and fold. On
 * touch a tab grows a step, to 36px, its hit area reaching 44px.
 *
 * On `compact` it is a 48px bar: a back chevron to the nearest crumb above that links somewhere, or
 * to `back`, the view the page was opened from (none on a tab's own page), the last crumb as the
 * page's title at `text-title`, the badges as icons, then one icon action and a ⋯ (`compactEndOf`).
 * The `tabs` become a 40px row under the bar that scrolls sideways, its current tab underlined, and
 * the actions' `controls` a row under that, which scrolls away with the page.
 *
 * It names the browser tab after the crumbs, most specific first, "Members · Beacon";
 * `documentTitle` names it instead, as a ticket's key and title do, and Beacon still follows.
 *
 * `heading="body"` is for a page whose body holds its hero title (a ticket's, an Epic's), which is
 * then the page's one h1, so the last crumb is plain text rather than a second; the compact bar
 * shows it quietly, as the key it is.
 *
 * While `pending` (a refetch keeping the old figures, on a page with no toolbar to say it), a
 * spinner leads the actions, as it leads a PageToolbar's end.
 */
export function PageHeader({
  crumbs,
  tabs,
  badges = [],
  actions,
  pending = false,
  documentTitle,
  heading = "last-crumb",
  back,
}: {
  crumbs: readonly Crumb[]
  tabs?: readonly HeaderTab[]
  badges?: readonly (keyof typeof headerBadges)[]
  actions?: PageActions | undefined
  pending?: boolean
  documentTitle?: string
  /** Which is the page's h1: its last crumb (the default), or a hero title in its body. */
  heading?: "last-crumb" | "body"
  /** Where the compact back chevron goes in place of the crumbs (`backOf`). */
  back?: PageBack | undefined
}) {
  const { compact } = useViewport()
  const tabbed = tabs !== undefined && tabs.length > 0
  useRegistered("header")
  useRegistered("tabs", compact && tabbed)
  const title = documentTitleOf(crumbs, documentTitle)
  useEffect(() => {
    document.title = title
  }, [title])
  const [width, measure] = useMeasuredWidth()

  if (compact)
    return (
      <CompactHeader
        crumbs={crumbs}
        tabs={tabbed ? tabs : undefined}
        badges={badges}
        actions={actions}
        pending={pending}
        heading={heading}
        back={back}
      />
    )

  return (
    <HeaderWidthContext value={width}>
      <header
        ref={measure}
        className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-4"
      >
        {/* Where the row is short, the crumbs above the page give way whole, the farthest first,
            before the page's own name is cut short: the row is laid from its end (`order`, so
            the DOM and a screen reader keep the crumbs' order) and wraps what doesn't fit onto
            a second line the nav hides. Alone, the page's name truncates. */}
        <nav
          aria-label="Breadcrumb"
          className="-mx-0.5 flex h-8 min-w-0 flex-row-reverse flex-wrap content-start items-center justify-end gap-x-0.5 gap-y-2 overflow-hidden px-0.5 py-0.5"
        >
          {crumbs.map((crumb, index) => {
            const order = { order: crumbs.length - 1 - index }
            if (index < crumbs.length - 1)
              return (
                <span key={index} style={order} className="flex min-w-0 items-center gap-0.5">
                  <CrumbItem crumb={crumb} last={false} />
                  <ChevronRightIcon
                    aria-hidden
                    className="size-3.5 shrink-0 text-subtle-foreground"
                  />
                </span>
              )
            // The last crumb is the page's name, so it is the page's one h1, for a screen reader's
            // heading list and for a route change to land on, at the crumb's own size; unless the
            // body's hero title is that h1.
            return heading === "last-crumb" ? (
              <h1 key={index} style={order} className="flex min-w-0">
                <CrumbItem crumb={crumb} last />
              </h1>
            ) : (
              <span key={index} style={order} className="flex min-w-0">
                <CrumbItem crumb={crumb} last />
              </span>
            )
          })}
        </nav>
        {badges.length > 0 && (
          <div className="ml-1 flex shrink-0 items-center gap-1.5">
            {badges.map((badge) => {
              const { variant, Icon, label } = headerBadges[badge]
              return (
                <Badge key={badge} variant={variant}>
                  <Icon aria-hidden />
                  {label}
                </Badge>
              )
            })}
          </div>
        )}
        {tabbed && (
          <>
            <Separator orientation="vertical" className="mx-1 h-4 data-vertical:self-center" />
            <nav aria-label="Views" className="flex shrink-0 items-center gap-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.href}
                  to={tab.href}
                  aria-current={tab.current ? "page" : undefined}
                  className={cn(
                    "inline-flex h-7 touch-target items-center rounded-md px-2 text-ui font-medium touch:h-9 transition-colors duration-100 ease-snappy focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                    tab.current
                      ? "bg-selected text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          </>
        )}
        {(anyOf(actions) || pending) && (
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {pending && <Updating />}
            {actions?.controls}
            {actions && <HeaderActions actions={actions} />}
          </div>
        )}
      </header>
    </HeaderWidthContext>
  )
}

/**
 * A page's actions as the desk's header draws them, without their `controls`: its secondary
 * buttons, its primary one and its ⋯, side by side for the bar to space. A bar that stands where a
 * PageHeader would (the Ticket peek's) draws a page's actions with it.
 */
export function HeaderActions({ actions }: { actions: PageActions }) {
  const { primary, secondary = [], more = [], moreLabel } = actions
  return (
    <>
      {secondary.map((action, index) => (
        <DeskSecondary key={index} action={action} />
      ))}
      {primary && <DeskPrimary action={primary} />}
      {more.length > 0 && <RowActions items={more} reveal="always" label={moreLabel} />}
    </>
  )
}

/** An action's icon at the start of its button, or a spinner while it is under way. */
function StartGlyph({ action }: { action: HeaderAction }) {
  return action.busy ? (
    <Loader2Icon data-icon="inline-start" className="animate-spin" />
  ) : (
    <action.icon data-icon="inline-start" />
  )
}

/** The words of a desk button: its icon, label and count. */
function ButtonWords({ action }: { action: HeaderAction }) {
  return (
    <>
      <StartGlyph action={action} />
      <span className="tabular-nums">{action.label}</span>
      {action.count !== undefined && <Badge>{action.count}</Badge>}
    </>
  )
}

/** The page's one primary action on the desk: a small filled Button, its Kbd inside. */
function DeskPrimary({ action }: { action: HeaderAction }) {
  const words = (
    <>
      <ButtonWords action={action} />
      {action.shortcut && <Kbd shortcut={action.shortcut} />}
    </>
  )
  return action.href ? (
    <ButtonLink to={action.href} size="sm">
      {words}
    </ButtonLink>
  ) : (
    <Button
      size="sm"
      {...dialogTrigger(action)}
      disabled={action.disabled || action.busy}
      onClick={action.onSelect}
    >
      {words}
    </Button>
  )
}

/** The host that has a confirmation draw itself as a labelled button and its dialog. */
const asButton = { part: "button" } as const

/** A secondary action on the desk, in its look. */
function DeskSecondary({ action }: { action: SecondaryAction }) {
  if ("kind" in action)
    return <MenuConfirmationContext value={asButton}>{action.confirmation}</MenuConfirmationContext>
  if (action.look === "icon") return <IconShortcut action={action} />
  if (action.look === "onward")
    return (
      <ButtonLink to={action.href ?? "/"} variant="ghost" size="sm">
        {action.label}
        <ArrowRightIcon data-icon="inline-end" />
      </ButtonLink>
    )
  const variant = action.look === "quiet" ? "ghost" : "outline"
  if (action.href)
    return (
      <ButtonLink to={action.href} variant={variant} size="sm">
        <ButtonWords action={action} />
      </ButtonLink>
    )
  const button = (
    <Button
      variant={variant}
      size="sm"
      {...dialogTrigger(action)}
      disabled={action.disabled || action.busy}
      onClick={action.onSelect}
    />
  )
  if (!action.shortcut) return cloneElement(button, {}, <ButtonWords action={action} />)
  // Beside the filled primary, a second Kbd would compete with its own, so it waits in a tooltip.
  return (
    <Tooltip>
      <TooltipTrigger render={button}>
        <ButtonWords action={action} />
      </TooltipTrigger>
      <TooltipContent>
        {action.label}
        <Kbd shortcut={action.shortcut} />
      </TooltipContent>
    </Tooltip>
  )
}

/** A ghost icon button named by its tooltip, which says "Copied" once it has copied its value. */
function IconShortcut({ action }: { action: HeaderAction & { copies?: string } }) {
  const { copied, copy } = useCopied(action.copies ?? "")
  const select = action.copies === undefined ? action.onSelect : () => void copy()
  return (
    <Tooltip>
      <TooltipTrigger
        closeOnClick={false}
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={action.label}
            disabled={action.disabled}
            onClick={select}
          />
        }
      >
        <action.icon />
      </TooltipTrigger>
      <TooltipContent>
        {copied ? (
          "Copied"
        ) : (
          <>
            {action.label}
            {action.shortcut && <Kbd shortcut={action.shortcut} />}
          </>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

/** The compact bar's icon buttons and back chevron: 40px, their hit areas reaching 44px. */
const barButton = cn(buttonVariants({ variant: "ghost", size: "icon" }), "size-10 text-foreground")

/** The compact header: the bar, then its tab row, then the page's controls. */
function CompactHeader({
  crumbs,
  tabs,
  badges,
  actions,
  pending,
  heading,
  back: own,
}: {
  crumbs: readonly Crumb[]
  tabs: readonly HeaderTab[] | undefined
  badges: readonly (keyof typeof headerBadges)[]
  actions: PageActions | undefined
  pending: boolean
  heading: "last-crumb" | "body"
  back: PageBack | undefined
}) {
  const navigate = useNavigate()
  const { tabRoot } = useContext(PageChromeContext)
  const back = backOf(crumbs, { tabRoot, back: own })
  const page = crumbs.at(-1)
  const { primary, secondary = [], more = [], moreLabel, controls } = actions ?? {}
  const end = compactEndOf(primary, [...secondary.filter((action) => !deskOnly(action)), ...more])
  const menu = end.menu.map((entry): RowAction => {
    if ("kind" in entry) return entry
    const { label, icon, href, onSelect, count, disabled, busy }: HeaderAction = entry
    return {
      label,
      icon,
      onSelect: href ? () => void navigate(href) : (onSelect ?? (() => undefined)),
      ...(count !== undefined && { count }),
      ...((disabled || busy) && { disabled: true }),
    }
  })
  const Title = heading === "last-crumb" ? "h1" : "span"

  return (
    <>
      <header className="sticky top-0 z-30 shrink-0 bg-background">
        <div
          className={cn(
            "flex h-12 items-center gap-1 px-1",
            !tabs && "border-b border-border-subtle",
          )}
        >
          {back && (
            <Link
              to={back.href}
              {...("state" in back && { state: back.state })}
              // Back to the view the page was opened from goes Back, so it keeps its place; any
              // other press (a long one, a modified click) still has its address.
              {...("returns" in back &&
                back.returns && {
                  onClick: (event: MouseEvent) => {
                    if (!opensInPlace(event)) return
                    event.preventDefault()
                    void navigate(-1)
                  },
                })}
              aria-label={`Back to ${back.label}`}
              // Its box is a hit area; the glyph sits close to the title, as a navigation bar's does.
              className={cn(barButton, "-mr-2")}
            >
              <ChevronLeftIcon aria-hidden className="size-6" />
            </Link>
          )}
          {page?.title === "" ? (
            <Title className="sr-only">{page.label}</Title>
          ) : (
            page && (
              <Title
                className={cn(
                  "flex min-w-0 items-center gap-2 pr-1",
                  !back && "pl-3",
                  heading === "last-crumb"
                    ? "text-title font-semibold text-foreground"
                    : "text-ui font-medium text-muted-foreground tabular-nums",
                )}
              >
                {page.glyph?.kind === "project" && (
                  <IdentityTile projectKey={page.glyph.projectKey} size={20} />
                )}
                {page.glyph?.kind === "person" && <PersonGlyph glyph={page.glyph} size={20} />}
                {page.glyph?.kind === "beacon" && <BeaconMark size={20} />}
                {page.detail ? (
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate leading-tight">{page.title ?? page.label}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">
                      {page.detail}
                    </span>
                  </span>
                ) : (
                  <span className="truncate">{page.title ?? page.label}</span>
                )}
                {page.count !== undefined && (
                  <span className="text-ui font-normal text-subtle-foreground tabular-nums">
                    {page.count}
                  </span>
                )}
              </Title>
            )
          )}
          {badges.map((badge) => {
            const { Icon, label } = headerBadges[badge]
            return (
              <Toggletip key={badge} tip={label}>
                <button
                  type="button"
                  aria-label={label}
                  className="inline-flex size-6 shrink-0 touch-target items-center justify-center rounded-md text-subtle-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Icon aria-hidden className="size-4" />
                </button>
              </Toggletip>
            )
          })}
          <div className="ml-auto flex shrink-0 items-center">
            {pending && (
              <span className="flex px-2">
                <Updating />
              </span>
            )}
            {end.icon && <BarAction action={end.icon} />}
            {menu.length > 0 && (
              <RowActions items={menu} reveal="always" size="bar" label={moreLabel} />
            )}
          </div>
        </div>
        {tabs && <TabRow tabs={tabs} />}
      </header>
      {controls !== undefined && (
        <div className="flex flex-wrap items-center gap-2 px-4 pt-4">{controls}</div>
      )}
    </>
  )
}

/**
 * The compact bar's one icon action, in its `barIcon` if it has one, named for a screen reader by
 * its label: the page's primary, or the one plain entry its ⋯ would have held.
 */
function BarAction({ action }: { action: HeaderAction }) {
  const Icon = action.barIcon ?? action.icon
  const glyph = action.busy ? (
    <Loader2Icon aria-hidden className="size-5 animate-spin" />
  ) : (
    <Icon aria-hidden className="size-5" />
  )
  return action.href ? (
    <Link to={action.href} aria-label={action.label} className={barButton}>
      {glyph}
    </Link>
  ) : (
    <Button
      variant="ghost"
      size="icon"
      aria-label={action.label}
      {...dialogTrigger(action)}
      disabled={action.disabled || action.busy}
      onClick={action.onSelect}
      className="size-10 text-foreground"
    >
      {glyph}
    </Button>
  )
}

/**
 * The compact header's tabs: a 40px row under the bar that scrolls sideways when they outgrow the
 * screen, the current one in the foreground and underlined, and scrolled into view as it opens.
 */
function TabRow({ tabs }: { tabs: readonly HeaderTab[] }) {
  const current = useRef<HTMLAnchorElement>(null)
  const href = tabs.find((tab) => tab.current)?.href
  useLayoutEffect(() => {
    const tab = current.current
    const row = tab?.parentElement
    if (tab && row && tab.offsetLeft + tab.offsetWidth > row.clientWidth)
      row.scrollLeft = tab.offsetLeft
  }, [href])
  return (
    <nav
      aria-label="Views"
      className="flex h-10 items-stretch overflow-x-auto border-b border-border-subtle px-1 no-scrollbar"
    >
      {tabs.map((tab) => (
        <Link
          key={tab.href}
          ref={tab.current ? current : undefined}
          to={tab.href}
          aria-current={tab.current ? "page" : undefined}
          className={cn(
            "relative inline-flex shrink-0 touch-target items-center px-3 text-ui font-medium whitespace-nowrap transition-colors duration-100 ease-snappy outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            tab.current
              ? "text-foreground before:absolute before:inset-x-3 before:bottom-0 before:h-0.5 before:rounded-full before:bg-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

/**
 * The one spinner that says a refetch is under way while the old rows or figures stay on screen:
 * a PageHeader's, a PageToolbar's and a card DataTable's bar. Internal to the design system.
 */
export function Updating() {
  return (
    <Loader2Icon
      role="status"
      aria-label="Updating"
      className="size-3.5 animate-spin text-subtle-foreground"
    />
  )
}

/*
 * The six page widths. The PageHeader's crumbs sit at one x on every page, whatever the width, so
 * moving between pages never shifts the chrome. On `compact` every width runs to 16px gutters, and
 * `split` stacks its panes.
 */
const widths = {
  /** Full bleed; rows own their `px-6`. Projects, My tickets, Members. */
  list: "w-full",
  /** Full bleed, filling the panel below the chrome for the Board's own scroller. */
  board: "flex min-h-0 flex-1 flex-col",
  /**
   * Full bleed, side by side: its children are PagePanes. A ticket, Project settings, Groups. It
   * fills what the panel has left below the chrome, and never shrinks below its content: the
   * sticky panes stick only as far as the body they sit in runs, so it must run the page's length.
   * On `compact` the panes stack in their order.
   */
  split: "flex grow shrink-0 compact:flex-col",
  /** A column of prose and settings. Connections, Agent, Not found. */
  document: "mx-auto w-full max-w-3xl px-10 py-8 compact:px-4 compact:py-6",
  /** Wider, for a page of cards. Home. */
  wide: "mx-auto w-full max-w-5xl px-10 py-8 compact:px-4 compact:py-6",
  /** Widest, for tiles and charts. Spend. */
  dashboard: "mx-auto w-full max-w-6xl px-8 py-6 compact:px-4",
} as const

/** Which of a `split` body's `master` and `main` panes the compact layout shows, if one alone. */
const CompactShowsContext = createContext<"master" | "main" | undefined>(undefined)

/**
 * A page's content, under its chrome, at one of six widths. `dimmed` fades it while a refetch
 * keeps the previous data on screen. It fades in as it arrives.
 *
 * `compactShows` is for a `split` body of a `master` list and its detail: on `compact` it shows
 * the one pane the page's URL says (the list, or the item chosen from it), as two screens, rather
 * than stacking the detail under the whole list. Without it the panes stack.
 */
export function PageBody({
  width,
  dimmed = false,
  compactShows,
  children,
}: {
  width: keyof typeof widths
  dimmed?: boolean
  compactShows?: "master" | "main"
  children: ReactNode
}) {
  return (
    <CompactShowsContext value={compactShows}>
      <div
        data-slot="page-body"
        className={cn(
          "animate-in transition-opacity duration-150 fade-in",
          widths[width],
          // On `compact` nothing in a body pans the page sideways, its chrome with it: what is
          // wider than a phone scrolls in a box of its own, and anything else is clipped. A clip,
          // not a scroller, so what sticks in the body still sticks to the page.
          "compact:overflow-x-clip",
          dimmed && "opacity-70",
        )}
      >
        {children}
      </div>
    </CompactShowsContext>
  )
}

/*
 * The panes of a `split` body, sticking on the ladder's `underHeader` rung. `nav` sticks under the
 * header beside the scrolling `main`. A `rail` runs the page's height, so its border rules the
 * whole page, while what it holds sticks under the header. A `master` list runs the page's height
 * beside its detail, its border with it, while its list sticks under the header at the height the
 * panel shows there (`belowHeader`) and scrolls on its own, so a list longer than the screen can be
 * read to its end without scrolling the detail to its end.
 *
 * On `compact` they stack, each the screen's width, in their order: the `nav` a bar stuck under the
 * header (its AnchorNav a row of chips), then `master` and `main` (or the one `compactShows`
 * says), then the `rail` after `main`, ruled off above, none of them sticking or scrolling alone.
 */
const panes = {
  nav: "w-48 shrink-0 sticky self-start pt-8 pl-6 compact:z-20 compact:w-full compact:self-stretch compact:p-0",
  master: "w-72 shrink-0 border-r border-border-subtle compact:w-full compact:border-r-0",
  main: "min-w-0 flex-1",
  rail: "w-72 shrink-0 border-l border-border-subtle compact:w-full compact:border-t compact:border-l-0",
} as const

/**
 * The column a `main` pane holds its content in, at the pane's own gutters: `settings`, a column
 * of sections as wide as a settings page reads (Project settings); `fill`, the pane's whole width
 * (the kitchen sink). Without one, the page lays out the pane itself.
 */
const columns = {
  settings: "max-w-2xl px-10 py-8 compact:px-4 compact:py-6",
  fill: "px-10 py-8 compact:px-4 compact:py-6",
} as const

/** One pane of a `split` PageBody, by the part it plays; a `main` one may hold a `column`. */
export function PagePane({
  role,
  column,
  children,
}:
  | { role: "main"; column?: keyof typeof columns; children: ReactNode }
  | { role: Exclude<keyof typeof panes, "main">; column?: never; children: ReactNode }) {
  const { underHeader, belowHeader } = useLadder()
  const compactShows = useContext(CompactShowsContext)
  const hidden =
    (role === "master" && compactShows === "main") || (role === "main" && compactShows === "master")
  return (
    <div
      data-slot="page-pane"
      data-role={role}
      className={cn(panes[role], role === "nav" && underHeader, hidden && "compact:hidden")}
    >
      {role === "master" ? (
        <div
          className={cn(
            "sticky overflow-y-auto overscroll-contain",
            underHeader,
            belowHeader,
            "compact:static compact:h-auto compact:overflow-visible",
          )}
        >
          {children}
        </div>
      ) : role === "rail" ? (
        <div className={cn("sticky px-4 py-6", underHeader, "compact:static")}>{children}</div>
      ) : column ? (
        <div className={columns[column]}>{children}</div>
      ) : (
        children
      )}
    </div>
  )
}
